import { Alert, AlertTitle, Button, Grid, Paper } from '@mui/material';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useEffect, useMemo, useState } from 'react';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import SearchField from 'src/shared/components/form/searchField';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { useGlobals } from 'src/shared/contexts/appContext';
import PaymentTransactionsDataGrid from '../components/paymentTransactionsDataGrid';
import PaymentPageTabNavigation from '../components/paymentTabNavigation';
import { PaymentGroupedByDate } from '../models/paymentGroupedByDate';
import { PaymentTransactionModal } from '../components/paymentTransationDetailModal';
import { PaymentTransactionSearchFilter } from '../models/paymentTransactionSearchFilter';
import { PaymentTransactionCreateModal } from '../components/paymentTransactionCreateModal';

export const PaymentTransactionsPage = () => {
  const { t } = useTranslation(['invoice']);
  const isMounted = useIsMounted();
  const { setDateStamp } = useGlobals();
  const [createMode, setCreateMode] = useState<boolean>(false);
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [paymentTransactionSearchFilter, setPaymentTransactionSearchFilter] = useLocalStorage(
    LocalStorageKeys.PaymentTransactionSearchFilter,
    {} as unknown as PaymentTransactionSearchFilter,
  );

  const searchFilter: PaymentTransactionSearchFilter = useMemo(() => {
    return {
      searchString: paymentTransactionSearchFilter?.searchString,
      ownerId: paymentTransactionSearchFilter?.ownerId,
    } as unknown as PaymentTransactionSearchFilter;
  }, [paymentTransactionSearchFilter?.ownerId, paymentTransactionSearchFilter?.searchString]);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedPaymentTransaction, setSelectedPaymentTransaction] = useState<PaymentGroupedByDate>();

  const handleRowClicked = (item: PaymentGroupedByDate) => {
    setSelectedPaymentTransaction(item);
    setShowModal(true);
  };

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setPaymentTransactionSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setPaymentTransactionSearchFilter((prev) => ({ ...prev, ...{ searchString: tempSearchString } }));
      } else {
        setPaymentTransactionSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
    }
  }, [isMounted, searchInitialized, setPaymentTransactionSearchFilter, tempSearchString]);

  return (
    <>
      <GridWrapper>
        <Grid item xs>
          <MainContentHeader title={t('invoice:transactionPage.pageTitle')} subtitle={t('invoice:transactionPage.pageDescription')} />
        </Grid>
        <Grid item xs='auto' style={{ gridColumnGap: '5px' }}>
          <Button onClick={() => setCreateMode(true)} variant='contained' color='secondary' startIcon={<Add />} disableElevation>
            {t('invoice:transactionPage.createTransactionButton')}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <PaymentPageTabNavigation />
            <PageFilterSection
              leftContent={
                <SearchField
                  initialValue={searchFilter.searchString || ''}
                  disabled={!searchFilter.ownerId || searchFilter.ownerId === 0}
                  onSearch={(newSearchString) => {
                    setSearchInitialized(true);
                    setTempSearchString(newSearchString);
                  }}
                />
              }
              rightContent={
                <InvoiceGroups
                  callback={(ownId) => {
                    setPaymentTransactionSearchFilter((prev) => ({ ...prev, ...{ ownerId: ownId } }));
                  }}
                  autoSelect
                  ownerId={searchFilter.ownerId}
                />
              }
            />
            {!!searchFilter.ownerId && searchFilter.ownerId > 0 && (
              <PaymentTransactionsDataGrid searchFilter={searchFilter} rowClicked={handleRowClicked} />
            )}
            {(!searchFilter.ownerId || searchFilter.ownerId === 0) && (
              <Alert severity='info'>
                <AlertTitle>{t('invoice:transactionPage.selectOwnerIdWarningTitle')}</AlertTitle>
                {t('invoice:transactionPage.selectOwnerIdWarningText')}
              </Alert>
            )}
          </Paper>
        </Grid>
      </GridWrapper>
      {selectedPaymentTransaction && (
        <PaymentTransactionModal
          modalOpen={showModal}
          invoicePayments={selectedPaymentTransaction.invoicePayments}
          onSave={() => setShowModal(false)}
          onClose={() => setShowModal(false)}
        />
      )}
      {createMode && (
        <PaymentTransactionCreateModal
          onClose={() => setCreateMode(false)}
          onSave={() => {
            setCreateMode(false);
            setDateStamp(new Date());
          }}
          modalOpen={createMode}
        />
      )}
    </>
  );
};
